import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Select, Switch, Card, message } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Mission, Reward } from '../../types/missions';
import { missionsService } from '../../services/missionsService';
import { supabase } from '../../lib/supabase';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';

const { Option } = Select;

interface FormValues extends Omit<Mission, 'id' | 'createdAt' | 'updatedAt'> {}

interface CardData {
  id: number;
  name: string;
  imageUrl: string;
  rarity?: string;
  set?: string;
}

interface SetData {
  id: string;  // UUID
  name: string;
  display_name: string;
}

interface RewardData {
  id: string;  // UUID
  name: string;
  imageUrl: string;
}

interface RewardFormItem {
  id: string;
  quantity: number;
}

// Composant pour l'option de carte avec prévisualisation
const CardOption: React.FC<{ card: CardData }> = ({ card }) => (
  <div className="flex items-center gap-2 py-1">
    <img 
      src={card.imageUrl} 
      alt={card.name} 
      className="w-10 h-14 object-contain"
    />
    <div className="flex-1 min-w-0">
      <div className="font-medium text-sm truncate">{card.name}</div>
      <div className="text-xs text-gray-500 flex items-center gap-1">
        <span>{card.set}</span>
        {card.rarity && (
          <>
            <span>•</span>
            <span>{card.rarity}</span>
          </>
        )}
      </div>
    </div>
  </div>
);

const CreateEditMission: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [sets, setSets] = useState<SetData[]>([]);
  const [allCards, setAllCards] = useState<CardData[]>([]);
  const [filteredCards, setFilteredCards] = useState<CardData[]>([]);
  const [rewards, setRewards] = useState<RewardData[]>([]);
  const [selectedCards, setSelectedCards] = useState<{ [key: string]: number }>({});
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (isEditing) {
      loadMission();
    }
  }, [id]);

  // Mettre à jour les cartes filtrées quand le set change
  const handleSetChange = (setId: string) => {
    const selectedSet = sets.find(set => set.id === setId);
    if (selectedSet) {
      const cardsInSet = allCards.filter(card => card.set === selectedSet.name);
      setFilteredCards(cardsInSet);
    } else {
      setFilteredCards([]);
    }
  };

  const loadInitialData = async () => {
    try {
      // Charger les sets
      const { data: setsData } = await supabase
        .from('sets')
        .select('id, name, display_name')
        .order('display_name');
      setSets(setsData || []);

      // Charger les cartes
      const { data: cardsData } = await supabase
        .from('cards')
        .select('id, card_fr, image, rarity, set');
      
      const formattedCards = (cardsData || []).map(card => ({
        id: card.id,
        name: card.card_fr || '',
        imageUrl: card.image ? `${SUPABASE_IMAGE_BASE_URL}${card.image}` : '',
        rarity: card.rarity,
        set: card.set
      }));
      
      setAllCards(formattedCards);
      // Initialiser les cartes filtrées si un set est déjà sélectionné
      const currentSetId = form.getFieldValue('setId');
      if (currentSetId && setsData) {
        const currentSet = setsData.find(set => set.id === currentSetId);
        if (currentSet) {
          setFilteredCards(formattedCards.filter(card => card.set === currentSet.name));
        }
      }

      // Charger les récompenses
      const { data: rewardsData } = await supabase
        .from('rewards')
        .select('id, name, image_url')
        .order('name');
      
      if (rewardsData) {
        const formattedRewards = rewardsData.map(reward => ({
          id: reward.id,
          name: reward.name,
          imageUrl: reward.image_url
        }));
        setRewards(formattedRewards);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      message.error('Erreur lors du chargement des données');
    }
  };

  const loadMission = async () => {
    try {
      setLoading(true);
      const mission = await missionsService.getMissionById(id!);
      form.setFieldsValue({
        name: mission.name,
        description: mission.description,
        setId: mission.setId,
        isActive: mission.isActive,
        requiredCardGroups: mission.requiredCardGroups,
        rewards: mission.rewards
      });
    } catch (error) {
      message.error('Erreur lors du chargement de la mission');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values: FormValues) => {
    try {
      setLoading(true);
      // S'assurer que les tableaux sont initialisés
      const formattedValues = {
        ...values,
        requiredCardGroups: values.requiredCardGroups || [],
        rewards: values.rewards || []
      };

      if (isEditing) {
        await missionsService.updateMission(id!, formattedValues);
        message.success('Mission mise à jour avec succès');
        navigate('/admin/missions');
      } else {
        const result = await missionsService.createMission(formattedValues);
        if (result) {
          message.success('Mission créée avec succès');
          navigate('/admin/missions');
        }
      }
    } catch (error) {
      console.error('Erreur détaillée:', error);
      message.error('Erreur lors de la sauvegarde de la mission');
    } finally {
      setLoading(false);
    }
  };

  const getCardById = (cardId: number) => {
    return allCards.find(card => card.id === cardId);
  };

  const handleCardSelect = (fieldName: number, cardFieldName: number, value: number) => {
    const key = `${fieldName}-${cardFieldName}`;
    setSelectedCards(prev => ({ ...prev, [key]: value }));
    form.setFieldValue(['requiredCardGroups', fieldName, 'cards', cardFieldName, 'cardId'], value);
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Modifier la Mission' : 'Créer une Mission'}
        </h1>
        <Button onClick={() => navigate('/admin/missions')} className="ml-4">
          Retour
        </Button>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ 
          isActive: true,
          requiredCardGroups: [],
          rewards: []
        }}
        className="space-y-6"
      >
        <Card className="shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Form.Item
              name="name"
              label="Nom"
              rules={[{ required: true, message: 'Le nom est requis' }]}
            >
              <Input className="w-full" />
            </Form.Item>

            <Form.Item
              name="setId"
              label="Set"
              rules={[{ required: true, message: 'Le set est requis' }]}
            >
              <Select 
                className="w-full"
                onChange={handleSetChange}
              >
                {sets.map(set => (
                  <Option key={set.id} value={set.id}>
                    {set.display_name || set.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'La description est requise' }]}
          >
            <Input.TextArea rows={4} className="w-full" />
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Card>

        <Card title="Cartes Requises" className="shadow-sm">
          <Form.List name="requiredCardGroups">
            {(fields, { add, remove }) => (
              <div className="space-y-6">
                {fields.map((field, index) => (
                  <Card
                    key={field.key}
                    className="bg-gray-50"
                    extra={
                      <MinusCircleOutlined
                        onClick={() => remove(field.name)}
                        className="text-red-500 hover:text-red-700 cursor-pointer"
                      />
                    }
                  >
                    <p className="font-medium text-gray-700 mb-4">Groupe de cartes {index + 1}</p>
                    <Form.List name={[field.name, 'cards']}>
                      {(cardFields, { add: addCard, remove: removeCard }) => (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {cardFields.map((cardField) => {
                              const key = `${field.name}-${cardField.name}`;
                              const cardId = selectedCards[key] || form.getFieldValue([
                                'requiredCardGroups',
                                field.name,
                                'cards',
                                cardField.name,
                                'cardId'
                              ]);
                              const selectedCard = cardId ? getCardById(cardId) : null;

                              return (
                                <div key={cardField.key} className="relative group">
                                  <div className="aspect-[3/4] bg-white rounded-lg shadow-sm overflow-hidden">
                                    {selectedCard ? (
                                      <>
                                        <img
                                          src={selectedCard.imageUrl}
                                          alt={selectedCard.name}
                                          className="w-full h-full object-contain transition-transform duration-200 group-hover:scale-105"
                                        />
                                        <div className="absolute top-2 right-2 flex flex-col gap-1">
                                          {selectedCard.rarity && (
                                            <span className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg text-xs font-medium">
                                              {selectedCard.rarity}
                                            </span>
                                          )}
                                        </div>
                                        <button
                                          onClick={() => {
                                            removeCard(cardField.name);
                                            const key = `${field.name}-${cardField.name}`;
                                            setSelectedCards(prev => {
                                              const newState = { ...prev };
                                              delete newState[key];
                                              return newState;
                                            });
                                          }}
                                          className="absolute top-2 left-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                        >
                                          <MinusCircleOutlined />
                                        </button>
                                      </>
                                    ) : (
                                      <div className="p-2">
                                        <Select
                                          showSearch
                                          optionFilterProp="children"
                                          className="w-full"
                                          placeholder="Sélectionner une carte"
                                          optionLabelProp="label"
                                          value={cardId}
                                          onChange={(value) => handleCardSelect(field.name, cardField.name, value as number)}
                                          filterOption={(input, option) => {
                                            const card = filteredCards.find(c => c.id === option?.value);
                                            return card?.name.toLowerCase().includes(input.toLowerCase()) || false;
                                          }}
                                        >
                                          {filteredCards.map(card => (
                                            <Option 
                                              key={card.id} 
                                              value={card.id}
                                              label={card.name}
                                            >
                                              <CardOption card={card} />
                                            </Option>
                                          ))}
                                        </Select>
                                      </div>
                                    )}
                                  </div>
                                  {selectedCard && (
                                    <div className="mt-2 text-center">
                                      <div className="text-sm font-medium text-gray-900 truncate">
                                        {selectedCard.name}
                                      </div>
                                      {selectedCard.set && (
                                        <div className="text-xs text-gray-500">
                                          {selectedCard.set}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <Button
                            type="dashed"
                            onClick={() => addCard()}
                            icon={<PlusOutlined />}
                            className="w-full"
                          >
                            Ajouter une carte alternative
                          </Button>
                        </div>
                      )}
                    </Form.List>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ cards: [] })}
                  icon={<PlusOutlined />}
                  className="w-full"
                >
                  Ajouter un groupe de cartes
                </Button>
              </div>
            )}
          </Form.List>
        </Card>

        <Card title="Récompenses" className="shadow-sm">
          <Form.List name="rewards">
            {(fields, { add, remove }) => (
              <div className="space-y-4">
                {fields.map((field) => (
                  <div key={field.key} className="flex items-center space-x-4">
                    <Form.Item
                      key={`${field.key}-id`}
                      name={[field.name, 'id']}
                      rules={[{ required: true, message: 'Sélectionnez une récompense' }]}
                      className="flex-grow mb-0"
                    >
                      <Select>
                        {rewards.map(reward => (
                          <Option key={reward.id} value={reward.id}>
                            {reward.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      key={`${field.key}-quantity`}
                      name={[field.name, 'quantity']}
                      rules={[{ required: true, message: 'La quantité est requise' }]}
                      className="w-32 mb-0"
                    >
                      <Input
                        type="number"
                        min={1}
                        placeholder="Quantité"
                      />
                    </Form.Item>
                    <MinusCircleOutlined
                      onClick={() => remove(field.name)}
                      className="text-red-500 hover:text-red-700 cursor-pointer"
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ id: '', quantity: 1 })}
                  icon={<PlusOutlined />}
                  className="w-full"
                >
                  Ajouter une récompense
                </Button>
              </div>
            )}
          </Form.List>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button onClick={() => navigate('/admin/missions')}>
            Annuler
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isEditing ? 'Mettre à jour' : 'Créer'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreateEditMission; 